// Register for home — "Refer a tenant" until 3 Nov 2026 (docs/plans/lettings-one-form.md, "26 Oct 2026, later: Lettings
// without sheets"): one advertised free flat and the tenant's application — the website's form in its four steps (29 Oct 2026; ApplicationSteps, the same rules
// and gating as the website: every field marked *, the passport and right to rent document uploaded on step 2, the three
// consents; a rent below the advert needs the reason, and becomes a rent request Lotus Living decides) — with Notes and,
// for Lotus Living, "Holding deposit already received" on step 4. POST /reservations/applications: on the flat's LIVE sheet it takes
// the sheet's row, otherwise a sheetless application is made (kept with the agent who referred it). Afterwards the drawer
// says "Registered — <name> is Under review" with a link to the application.
// Opened from Lettings → Available (/app/lettings/available?unit=<unitId>), a free flat on a development's Sheet tab, and "Register
// for home" on a completed viewing — staff or the agent who hosted it (&lead=<leadId>: the form starts from that viewing
// request's own form, so nothing is typed twice). It replaces the free-flat half of the sheet's old FlatModal.
// Since 3 Nov 2026 (docs/plans/lettings-ui-simplification.md) no intro line above the form, and sheets are import-only, so
// nothing here mentions them. The header's subtitle is "Step N of 4" (the title already names the flat); the done screen
// keeps the flat's one line. No "Filled in from …'s viewing request" note either (3 Nov 2026, later) — the prefill stays, and
// the rent offered starts at the advert.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { Button, Field, SkeletonRows, Textarea } from '../ui';
import { FormError, toMajor, toMinor } from '../forms/common';
import { useCan } from '../../hooks/usePermission';
import { formatMoney } from '../../lib/viz';
import { reservationsApi, floorLabel, bedsLabel, type AvailableFlat, type FlatFull } from '../../lib/reservations';
import { emptyApplication, fromLead, nameOf, moveInTooEarly, serverFieldError, toPayload, withFreeFrom, type ApplicationState } from '../../lib/applicationForm';
import { formDocsPayload, moneyBlock, siteFormErrors, stepLabel, useFormSteps, type FormDocs } from '../../lib/applicationSteps';
import { leadsApi } from '../../lib/lettings';
import { ApplicationSteps, StepButtons } from './ApplicationFields';
import { DrawerFooter, RecordDrawer } from './Drawer';

/** The flat's one line: development · floor · type · size · advert. */
const flatLine = (f: AvailableFlat) =>
  [f.propertyName, floorLabel(f.floor), bedsLabel(f.bedrooms, f.unitType), f.areaSqft ? `${f.areaSqft} sq ft` : null, `${formatMoney(f.advertMinor, f.currency)} pcm`].filter(Boolean).join(' · ');

export function ReferDrawer({ unitId, leadId, onClose }: { unitId: string; leadId?: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const isStaff = useCan('reservations.update');
  const canRefer = useCan('reservations.reserve');
  const q = useQuery({ queryKey: ['/lettings/available', { unitId }], queryFn: async () => (await reservationsApi.available({ unitId }))[0] ?? null });
  const lead = useQuery({ queryKey: ['/leads', leadId], queryFn: () => leadsApi.get(leadId!), enabled: !!leadId, retry: false });
  const [app, setApp] = useState<ApplicationState>(() => emptyApplication());
  const [prefilled, setPrefilled] = useState(false);
  if (lead.data && !prefilled) { setPrefilled(true); setApp(fromLead(lead.data)); }
  // The move-in starts at the day the flat is free (today when it is free now) and cannot go earlier; a viewing request's
  // own date stays when it is on or after that. Once the flat (and the request, when there is one) is in. The rent offered
  // starts at the advert (3 Nov 2026) unless the viewing request already named one.
  const [freed, setFreed] = useState(false);
  if (q.data && !freed && (!leadId || prefilled || lead.isError)) {
    setFreed(true);
    const flat = q.data;
    setApp((cur) => {
      const next = withFreeFrom(cur, flat.availableFrom, { notice: flat.underNotice, reset: !prefilled });
      return next.offeredRent.trim() || !flat.advertMinor ? next : { ...next, offeredRent: toMajor(flat.advertMinor) };
    });
  }
  const [docs, setDocs] = useState<FormDocs>({});
  const [notes, setNotes] = useState('');
  const [holding, setHolding] = useState(false);
  // What was referred, and the flat as it was (the list is refetched afterwards and no longer has it).
  const [done, setDone] = useState<{ r: FlatFull; flat: AvailableFlat } | null>(null);
  const run = useMutation({
    mutationFn: () => reservationsApi.refer({
      // The website's payload (the three consents stamped now, signed in the tenant's name) and its documents.
      unitId, form: toPayload(app), documents: formDocsPayload(app.nationality, docs), notes: notes.trim() || null, holdingDepositReceived: isStaff ? holding : undefined, leadId: lead.data ? leadId : null,
    }),
    onSuccess: (r) => {
      qc.setQueryData(['/reservations', r.id], r);
      for (const k of ['/lettings/available', '/reservations/applications', '/reservations/sheets', '/reservations/pipeline', '/reservations/mine', '/reservations', '/reservations/offers', '/lettings/viewings']) void qc.invalidateQueries({ queryKey: [k] });
      if (q.data) setDone({ r, flat: q.data });
    },
    // The flat's free date moved while the drawer was open: the move-in field says so under it.
    onError: (e) => { const early = moveInTooEarly(e); if (early) setApp((cur) => ({ ...cur, freeFrom: early.availableFrom, freeNotice: cur.freeNotice || !!q.data?.underNotice })); },
  });
  // The website's rules and documents (a viewing request's own uploads count), plus the reason for a rent under the advert.
  const advert = q.data?.advertMinor ?? 0;
  const onFile = lead.data?.formDocuments ?? [];
  const asked = toMinor(app.offeredRent) ?? null;
  const reasonMissing = asked !== null && asked < advert && !(app.offerReason ?? '').trim();
  const errs = { ...siteFormErrors(app, advert, { docs, onFile: onFile.map((d) => d.category) }), ...(reasonMissing ? { offerReason: 'Say why the rent offered is below the advert' } : {}) };
  const steps = useFormSteps(errs, { error: run.error, onSubmit: () => run.mutate(), blocks: moneyBlock(app, advert) });

  if (done) {
    const { r: made, flat: was } = done;
    const name = made.applicant?.name ?? nameOf(app);
    return (
      <RecordDrawer open onClose={onClose} eyebrow={`Register for home · ${was.reference}`} title="Registered" subtitle={flatLine(was)}>
        <DrawerFooter><Button variant="secondary" onClick={onClose}>Close</Button></DrawerFooter>
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-[13px] text-emerald-900">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <b>Registered — {name} is Under review.</b> Lotus Living checks and approves it; the tenant's link is emailed on approval.
            {made.rentRequestStatus === 'PENDING' && <> The rent asked is below the advert, so it is also on Offers.</>}
            <div className="mt-1.5"><Link to={`/app/lettings/applications?flat=${made.id}`} onClick={onClose} className="font-medium underline">Open the application</Link></div>
          </div>
        </div>
      </RecordDrawer>
    );
  }

  const f = q.data;
  if (!f) {
    return (
      <RecordDrawer open onClose={onClose} eyebrow="Register for home" title={q.isLoading ? 'Loading…' : 'Not available'}>
        {q.isLoading ? <SkeletonRows rows={6} cols={2} />
          : <p className="py-8 text-center text-[13px] text-ink-500">{q.error ? (q.error as Error).message : 'This flat is not free to register a tenant for — someone is on it, or it has no live advert. A flat is registered for from its published listing (Marketing → Listings).'}</p>}
      </RecordDrawer>
    );
  }

  return (
    <RecordDrawer open onClose={onClose} eyebrow={`Register for home · ${f.reference}`} title={f.headline || `Flat ${f.reference}`} subtitle={stepLabel(steps)}>
      <DrawerFooter>
        <Button variant="secondary" className="mr-auto" onClick={onClose}>Cancel</Button>
        <StepButtons steps={steps} submitLabel="Register for home" pending={run.isPending} disabled={!canRefer} />
      </DrawerFooter>
      <div className="space-y-4">
        {!canRefer && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">Your role cannot register a tenant for a home.</p>}
        <ApplicationSteps showStep={false} steps={steps} s={app} set={(p) => setApp((cur) => ({ ...cur, ...p }))} advert={advert} docs={docs} setDocs={setDocs} onFile={onFile} extra={<>
          <Field label="Notes"><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full" /></Field>
          {isStaff && <label className="flex items-center gap-2 rounded-lg border border-ink-200 px-3 py-2 text-[12.5px]"><input type="checkbox" checked={holding} onChange={(e) => setHolding(e.target.checked)} />Holding deposit already received — tick the first step now</label>}
        </>} />
        {/* A refusal about a field shows under it, on its step; anything else shows here. */}
        {!serverFieldError(run.error) && <FormError error={run.error} />}
      </div>
    </RecordDrawer>
  );
}
